import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { format, formatDistanceToNow } from 'date-fns'
import {
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  Button,
  CircularProgress,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  PageContainer,
  StyledCard,
  SectionHeader,
  PrimaryButton,
} from '../components/ui/StyledComponents'
import EditTeacherForm from '../components/admin/EditTeacherForm'
import ArchiveDialog from '../components/admin/ArchiveDialog'
import SnackbarMessage from '../components/SnackbarMessage'
import { useAuth } from '../context/AuthContext'
import supabase from '../supabaseClient'

function TeacherDetailPage() {
  const { teacherId } = useParams() // Get teacherId from URL
  const [teacher, setTeacher] = useState(null)
  const [classes, setClasses] = useState([])
  const [loading, setLoading] = useState(true)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  })
  const { userRole, user } = useAuth()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isAdmin = userRole === 'admin' || userRole === 'super_admin'

  // Fetch teacher demographic info
  useEffect(() => {
    const fetchTeacherInfo = async () => {
      setLoading(true)
      const { data, error } = await supabase
        .from('teachers')
        .select('*')
        .eq('id', teacherId)
        .maybeSingle() // Fetch the teacher data

      if (error) {
        console.error('Error fetching teacher info:', error)
      } else {
        setTeacher(data) // Set the teacher data in state
      }
      setLoading(false)
    }

    fetchTeacherInfo()
  }, [teacherId])

  // Fetch the classes taught by the teacher
  useEffect(() => {
    const fetchClasses = async () => {
      const { data, error } = await supabase
        .from('classes')
        .select('id, name, start_time, end_time, shift, start_date')
        .eq('teacher_id', teacherId)

      if (error) {
        console.error('Error fetching classes:', error)
      } else {
        setClasses(data)
      }
    }

    if (teacher) {
      fetchClasses()
    }
  }, [teacher, teacherId])

  const handleEditSuccess = () => {
    // Refresh teacher data
    const fetchTeacherInfo = async () => {
      const { data, error } = await supabase
        .from('teachers')
        .select('*')
        .eq('id', teacherId)
        .maybeSingle()

      if (error) {
        console.error('Error fetching teacher info:', error)
      } else {
        setTeacher(data)
      }
    }

    fetchTeacherInfo()
  }

  const handleToggleActive = () => {
    if (!teacher?.id) return

    // If currently active and trying to inactivate, show archive dialog
    if (teacher.is_active !== false) {
      setArchiveDialogOpen(true)
    } else {
      // If reactivating, proceed directly (no dialog needed)
      handleReactivate()
    }
  }

  const handleArchiveConfirm = async (reason) => {
    if (!teacher?.id) return

    setArchiveDialogOpen(false)
    setActionLoading(true)

    try {
      const { error: updateError } = await supabase
        .from('teachers')
        .update({
          is_active: false,
          archive_reason: reason,
          archived_by: user?.id || null,
          archived_at: new Date().toISOString(),
        })
        .eq('id', teacher.id)

      if (updateError) throw updateError

      // Refresh teacher data
      const { data, error: fetchError } = await supabase
        .from('teachers')
        .select('*')
        .eq('id', teacher.id)
        .maybeSingle()

      if (fetchError) throw fetchError
      setTeacher(data)

      setSnackbar({
        open: true,
        message: 'Teacher inactivated successfully',
        severity: 'success',
      })
    } catch (error) {
      console.error('Error inactivating teacher:', error)
      setSnackbar({
        open: true,
        message: 'Failed to inactivate teacher',
        severity: 'error',
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleReactivate = async () => {
    if (!teacher?.id) return

    setActionLoading(true)

    try {
      const { error: updateError } = await supabase
        .from('teachers')
        .update({
          is_active: true,
          archive_reason: null,
          archived_by: null,
          archived_at: null,
        })
        .eq('id', teacher.id)

      if (updateError) throw updateError

      // Refresh teacher data
      const { data, error: fetchError } = await supabase
        .from('teachers')
        .select('*')
        .eq('id', teacher.id)
        .maybeSingle()

      if (fetchError) throw fetchError
      setTeacher(data)

      setSnackbar({
        open: true,
        message: 'Teacher reactivated successfully',
        severity: 'success',
      })
    } catch (error) {
      console.error('Error reactivating teacher:', error)
      setSnackbar({
        open: true,
        message: 'Failed to reactivate teacher',
        severity: 'error',
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  return (
    <PageContainer>
      {loading ? (
        <StyledCard sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          <Typography 
            variant="body1"
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            Loading teacher details...
          </Typography>
        </StyledCard>
      ) : teacher ? (
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Teacher Demographic Information */}
          <Grid item xs={12} sm={4}>
            <StyledCard sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <SectionHeader sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }, mb: { xs: 1.5, sm: 2 } }}>
                Teacher Info
              </SectionHeader>
              <Box sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' }, mb: { xs: 1.5, sm: 2 } }}>
                <Avatar
                  alt={`${teacher.first_name} ${teacher.last_name}`}
                  src={teacher.photo_url}
                  sx={{ 
                    width: { xs: 100, sm: 110, md: 120 }, 
                    height: { xs: 100, sm: 110, md: 120 },
                  }}
                />
              </Box>
              <Typography 
                variant="h6"
                sx={{ 
                  fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
                  textAlign: { xs: 'center', sm: 'left' },
                }}
              >
                {teacher.first_name} {teacher.last_name}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mt: 1,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  textAlign: { xs: 'center', sm: 'left' },
                }}
              >
                <strong>Email:</strong> {teacher.email || 'Not provided'}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mt: 1,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  textAlign: { xs: 'center', sm: 'left' },
                }}
              >
                <strong>Phone:</strong> {teacher.phone || 'Not provided'}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mt: 1,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  textAlign: { xs: 'center', sm: 'left' },
                }}
              >
                <strong>Gender:</strong> {teacher.gender}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mt: 1,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  textAlign: { xs: 'center', sm: 'left' },
                }}
              >
                <strong>Join Date:</strong>{' '}
                {format(new Date(teacher.created_at), 'MM/dd/yyyy')}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mt: 1, 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'center', sm: 'center' }, 
                  gap: { xs: 0.5, sm: 1 },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                }}
              >
                <strong>Last Active:</strong>
                {teacher.last_active ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                    {formatDistanceToNow(new Date(teacher.last_active), { addSuffix: true })}
                    <Chip 
                      label={(() => {
                        const daysSinceActive = Math.floor((new Date() - new Date(teacher.last_active)) / (1000 * 60 * 60 * 24));
                        if (daysSinceActive === 0) return 'Active Today';
                        if (daysSinceActive <= 3) return 'Recently Active';
                        if (daysSinceActive <= 7) return 'Inactive';
                        return 'Long Inactive';
                      })()}
                      color={(() => {
                        const daysSinceActive = Math.floor((new Date() - new Date(teacher.last_active)) / (1000 * 60 * 60 * 24));
                        if (daysSinceActive === 0) return 'success';
                        if (daysSinceActive <= 3) return 'info';
                        if (daysSinceActive <= 7) return 'warning';
                        return 'error';
                      })()}
                      size="small"
                      sx={{
                        fontSize: { xs: '0.65rem', sm: '0.75rem' },
                        height: { xs: 18, sm: 20 },
                      }}
                    />
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                    Never logged in
                    <Chip 
                      label="Never Active" 
                      color="error" 
                      size="small"
                      sx={{
                        fontSize: { xs: '0.65rem', sm: '0.75rem' },
                        height: { xs: 18, sm: 20 },
                      }}
                    />
                  </Box>
                )}
              </Typography>
              {isAdmin && (
                <>
                  <PrimaryButton
                    variant="contained"
                    onClick={() => setIsEditDialogOpen(true)}
                    fullWidth
                    sx={{ 
                      mt: 2,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      padding: { xs: '10px 16px', sm: '12px 24px' },
                    }}
                  >
                    Edit Teacher Info
                  </PrimaryButton>
                  <Button
                    variant={teacher.is_active !== false ? 'outlined' : 'contained'}
                    color={teacher.is_active !== false ? 'error' : 'success'}
                    onClick={handleToggleActive}
                    disabled={actionLoading}
                    fullWidth
                    sx={{ 
                      mt: 1,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      padding: { xs: '10px 16px', sm: '12px 24px' },
                    }}
                  >
                    {actionLoading ? (
                      <CircularProgress size={isMobile ? 20 : 24} />
                    ) : teacher.is_active !== false ? (
                      'Inactivate Teacher'
                    ) : (
                      'Reactivate Teacher'
                    )}
                  </Button>
                </>
              )}
            </StyledCard>
          </Grid>

          {/* Classes Taught by the Teacher */}
          <Grid item xs={12} sm={8}>
            <StyledCard sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <SectionHeader sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }, mb: { xs: 1.5, sm: 2 } }}>
                Classes Taught
              </SectionHeader>
              <TableContainer
                sx={{
                  overflowX: { xs: 'auto', sm: 'visible' },
                  '&::-webkit-scrollbar': {
                    height: { xs: '6px', sm: '0px' },
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '3px',
                  },
                }}
              >
                <Table sx={{ minWidth: { xs: 500, sm: 'auto' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap' }}>
                        Class Name
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}>
                        Shift
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap' }}>
                        Start Date
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {classes.length > 0 ? (
                      classes.map((classItem) => (
                        <TableRow key={classItem.id}>
                          <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            <Box>
                              <Typography variant="body2" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                {classItem.name}
                              </Typography>
                              {isMobile && (
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem' }}>
                                  Shift: {classItem.shift}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, display: { xs: 'none', md: 'table-cell' } }}>
                            {classItem.shift}
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {format(
                              new Date(classItem.start_date),
                              'MM/dd/yyyy',
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell 
                          colSpan={3} 
                          align="center"
                          sx={{ 
                            py: { xs: 3, sm: 4 },
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                          }}
                        >
                          No classes assigned to this teacher
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </StyledCard>
          </Grid>
        </Grid>
      ) : (
        <StyledCard sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          <Typography 
            variant="body1"
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            Teacher not found
          </Typography>
        </StyledCard>
      )}

      {/* Edit Dialog */}
      <EditTeacherForm
        teacher={teacher}
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        onSuccess={handleEditSuccess}
      />

      {/* Archive Dialog */}
      <ArchiveDialog
        open={archiveDialogOpen}
        onClose={() => setArchiveDialogOpen(false)}
        onConfirm={handleArchiveConfirm}
        entityName="Teacher"
      />

      {/* Snackbar */}
      <SnackbarMessage
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
      />
    </PageContainer>
  )
}

export default TeacherDetailPage
